import React, { useState, useEffect } from 'react';
import { 
    CheckCircle, AlertTriangle, UserCheck, Bed, Plus, User, Users, Gift 
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const toArray = (response, keys = []) => {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    for (const key of keys) {
      if (Array.isArray(raw[key])) return raw[key];
    }
    if (Array.isArray(raw.data)) return raw.data;
  }
  return [];
};

const CheckInCheckout = () => {
  const [activeTab, setActiveTab] = useState('checkin_form'); 
  const [loading, setLoading] = useState(false);
  const [reglesTarifs, setReglesTarifs] = useState([]);
  const [chambresDisponibles, setChambresDisponibles] = useState([]);
  const [clientsBD, setClientsBD] = useState([]);
  const [civilitesList, setCivilitesList] = useState([]);

  const [composantsChambre, setComposantsChambre] = useState([]);
  const [composantsSelectionnes, setComposantsSelectionnes] = useState({});
  const [tarifNegocieManuel, setTarifNegocieManuel] = useState('');

  const [ignorerFraisEarly, setIgnorerFraisEarly] = useState(false);

  // États pour la recherche dynamique de chambre
  const [chambreRecherche, setChambreRecherche] = useState('');
  const [showChambreDropdown, setShowChambreDropdown] = useState(false);

  const aujourdHui = new Date().toLocaleDateString('fr-CA');

  const getLendemain = (dateStr) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const [clientForm, setClientForm] = useState({
    civilite_id: '', nom: '', nif: '', telephone: '', email: '', adresse: '', 
    piece_identite_type: 'CNI', piece_identite_numero: ''
  });

  const [sejourForm, setSejourForm] = useState({
    client_id: '', civilite_id: '', client_recherche: '', chambre_id: '', type_sejour: 'NUITEE',
    is_early_checkin: false, date_arrivee: aujourdHui, date_depart_prevue: getLendemain(aujourdHui),
    heure_arrivee: '14:00', heure_depart: '12:00', regle_tarif_id: '',
    nombre_adultes: 1, nombre_enfants: 0
  });

  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showFormClientDropdown, setShowFormClientDropdown] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchData = async (arr = aujourdHui, dep = sejourForm.date_depart_prevue) => {
    try {
      setLoading(true);
      const urlDispo = `/hebergement/reservations/disponibles?arrivee=${arr}&depart=${dep}`;

      const [reglesData, dbClients, chambresPhysiquesRes, disposRes, civilitesRes] = await Promise.all([
        API.get('/hebergement/regles-tarifs').catch(() => ({ data: [] })),
        API.get('/customers').catch(() => ({ data: [] })),
        API.get('/hebergement/chambres').catch(() => ({ data: [] })),
        API.get(urlDispo).catch(() => ({ data: [] })),
        API.get('/hebergement/civilites').catch(() => ({ data: [] }))
      ]);
         
      setReglesTarifs(toArray(reglesData, ['regles', 'data']));
      const listChambres = toArray(chambresPhysiquesRes, ['chambres', 'data']);
      const listDispos = toArray(disposRes, ['disponibles', 'chambres', 'data']);

      const mapChambres = new Map(listChambres.map(ch => [ch.id, ch]));
      setChambresDisponibles(listDispos.map(ch => {
        const id = ch?.id || ch?.chambre_id;
        return { ...(mapChambres.get(id) || ch), turnover_message: ch?.turnover_message || null };
      }));
         
      setClientsBD(toArray(dbClients, ['clients', 'customers', 'data']));

      const civilitesArr = toArray(civilitesRes, ['civilites', 'data']);
      setCivilitesList(civilitesArr);
    } catch (err) {
      console.error("Erreur chargement:", err);
      showToast("Erreur lors du chargement des données", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(aujourdHui, sejourForm.date_depart_prevue);
  }, [sejourForm.date_depart_prevue]);

  useEffect(() => {
    const fetchComposantsChambre = async () => {
      if (!sejourForm.chambre_id) {
        setComposantsChambre([]);
        setComposantsSelectionnes({});
        setTarifNegocieManuel('');
        return;
      }
      try {
        const res = await API.get(`/hebergement/chambres/${sejourForm.chambre_id}`);
        const items = res.data?.items || res.data?.composants || res.data?.chambre?.items || res.data?.chambre?.composants || [];
        const itemsSafe = Array.isArray(items) ? items : [];
        setComposantsChambre(itemsSafe);
           
        const initialSelection = {};
        itemsSafe.forEach((item, idx) => {
          initialSelection[item?.id ? `${item.id}-${idx}` : `comp-${idx}`] = true;
        });
        setComposantsSelectionnes(initialSelection);
      } catch (err) {
        console.error("Erreur chargement composants chambre:", err);
        setComposantsChambre([]);
      }
    };
    fetchComposantsChambre();
  }, [sejourForm.chambre_id]);

  const calculerTarifActuel = () => {
    let total = 0;
    composantsChambre.forEach((item, idx) => {
      const key = item?.id ? `${item.id}-${idx}` : `comp-${idx}`;
      if (composantsSelectionnes[key]) {
        const qte = Number(item?.quantite || item?.quantite_incluse || 1);
        const prixU = Number(item?.prix_unitaire || item?.prix_defaut || item?.prix || 0);
        total += qte * prixU;
      }
    });
    return total;
  };

  const calculerNombreNuites = (dateArrStr, dateDepStr, typeSejour) => {
    if (typeSejour === 'DAY_USE') return 1;
    const dateArr = new Date(dateArrStr);
    const dateDep = new Date(dateDepStr);
    const diffTime = dateDep - dateArr;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 0 && dateArrStr === aujourdHui && dateDepStr === aujourdHui) {
      return 1;
    }
    return Math.max(1, diffDays);
  };

  const tarifDeBaseCalcule = calculerTarifActuel();
  const tarifNuitFinal = tarifNegocieManuel !== '' ? Number(tarifNegocieManuel) : tarifDeBaseCalcule;

  const handleEnregistrerClient = async (e) => {
    e.preventDefault();
    if (!clientForm.nom.trim()) {
      showToast("Le nom ou la raison sociale du client est obligatoire", "error");
      return;
    }
    if (!clientForm.civilite_id) {
      showToast("La civilité est obligatoire", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        civilite_id: clientForm.civilite_id,
        nom: clientForm.nom.toUpperCase(),
        nif: clientForm.nif || '', telephone: clientForm.telephone || '',
        email: clientForm.email || '', adresse: clientForm.adresse || '',
        piece_identite_type: clientForm.piece_identite_type || 'CNI',
        piece_identite_numero: clientForm.piece_identite_numero || '', is_active: 1
      };
      const response = await API.post('/customers', payload);
      showToast("Client enregistré avec succès !");
      const nouveauClient = response.data?.client || response.data;
      await fetchData(aujourdHui, sejourForm.date_depart_prevue);
       
      if (nouveauClient && nouveauClient.id) {
        const nomPropre = (clientForm.nom || '').trim().toUpperCase();
        setSejourForm(prev => ({ 
          ...prev, 
          client_id: nouveauClient.id, 
          civilite_id: clientForm.civilite_id,
          client_recherche: nomPropre 
        }));
      }

      setClientForm({ civilite_id: '', nom: '', nif: '', telephone: '', email: '', adresse: '', piece_identite_type: 'CNI', piece_identite_numero: '' });
      setActiveTab('checkin_form');
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de l'enregistrement du client", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleValiderAffectationChambre = async (e) => {
    e.preventDefault();
    if (!sejourForm.client_id) { showToast("Veuillez sélectionner un client enregistré", "error"); return; }
    if (!sejourForm.chambre_id) { showToast("Veuillez sélectionner une chambre", "error"); return; }

    setLoading(true);
    try {
      const diffNuites = calculerNombreNuites(aujourdHui, sejourForm.date_depart_prevue, sejourForm.type_sejour);
      const montantTotalCalcule = tarifNuitFinal * diffNuites;

      const composantsActifs = composantsChambre.map((comp, idx) => {
        const key = comp.id ? `${comp.id}-${idx}` : `comp-${idx}`;
        if (composantsSelectionnes[key] === false) return null;
        return {
          composant_id: comp.id || comp.composant_id || null,
          libelle: comp.designation || comp.libelle || comp.nom || 'Prestation',
          prix_unitaire: Number(comp.prix_unitaire || comp.prix_defaut || comp.prix || 0),
          soumis_tva: comp.soumis_tva !== undefined ? Number(comp.soumis_tva) : 1,
          taux_tva: comp.taux_tva !== undefined ? Number(comp.taux_tva) : 18.0
        };
      }).filter(Boolean);

      const payload = {
        ...sejourForm, 
        date_arrivee: aujourdHui, 
        tarif_nuit: tarifNuitFinal,
        montant_total: montantTotalCalcule, 
        nombre_nuites: diffNuites, 
        composants: composantsActifs,
        nombre_adultes: parseInt(sejourForm.nombre_adultes) || 1,
        nombre_enfants: parseInt(sejourForm.nombre_enfants) || 0,
        ignorer_frais_early: ignorerFraisEarly
      };

      await API.post('/hebergement/reservations/sejours', payload);
      showToast("Check-in et affectation de chambre réussis !");
       
      setSejourForm({
        client_id: '', civilite_id: '', client_recherche: '', chambre_id: '', type_sejour: 'NUITEE',
        is_early_checkin: false, date_arrivee: aujourdHui, date_depart_prevue: getLendemain(aujourdHui),
        heure_arrivee: '14:00', heure_depart: '12:00', regle_tarif_id: '',
        nombre_adultes: 1, nombre_enfants: 0
      });
      setChambreRecherche('');
      setComposantsChambre([]);
      setTarifNegocieManuel('');
      setIgnorerFraisEarly(false);
      await fetchData(aujourdHui, getLendemain(aujourdHui));
    } catch (err) {
      showToast(err.response?.data?.error || err.response?.data?.message || "Erreur lors de l'affectation", "error");
    } finally {
      setLoading(false);
    }
  };

  const clientsFiltres = (Array.isArray(clientsBD) ? clientsBD : []).filter(c => {
    if (!c) return false;
    const recherche = (sejourForm.client_recherche || '').toLowerCase();
    return (c.nom && String(c.nom).toLowerCase().includes(recherche)) || (c.telephone && String(c.telephone).includes(sejourForm.client_recherche || ''));
  });

  const clientsFormFiltres = (Array.isArray(clientsBD) ? clientsBD : []).filter(c => {
    if (!c) return false;
    return c.nom && String(c.nom).toLowerCase().includes((clientForm.nom || '').toLowerCase());
  });

  // Filtrage des chambres disponibles avec une limite maximale de 10 résultats
  const chambresFiltrees = (Array.isArray(chambresDisponibles) ? chambresDisponibles : []).filter(ch => {
    if (!ch) return false;
    const recherche = (chambreRecherche || '').toLowerCase();
    const numero = String(ch.numero || '').toLowerCase();
    const categorie = String(ch.categorie_nom || ch.categorie || '').toLowerCase();
    return numero.includes(recherche) || categorie.includes(recherche);
  }).slice(0, 10);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f8fafc' }}>
      <Sidebar />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserCheck size={20} color="#2563eb" /> CHECK-IN & ENREGISTREMENT CLIENT
            </h1>
            <div style={{ color: '#64748b', fontSize: '12px' }}>
              Création des profils clients et affectation des chambres (Walk-in)
            </div>
          </div>

          <div style={{ display: 'flex', background: '#e2e8f0', padding: '4px', borderRadius: '10px', gap: '4px' }}>
            <button onClick={() => setActiveTab('checkin_form')} style={activeTab === 'checkin_form' ? btnTabActiveStyle : btnTabStyle}>
              <Bed size={14} /> Effectuer un Check-in
            </button>
            <button onClick={() => setActiveTab('client_form')} style={activeTab === 'client_form' ? btnTabActiveStyle : btnTabStyle}>
              <User size={14} /> Enregistrer un Client
            </button>
          </div>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 30px', boxSizing: 'border-box' }}>
          {activeTab === 'client_form' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '900px', margin: '0 auto' }}>
              <div style={{ ...contentCardStyle, padding: '24px', border: '2px solid #2563eb' }}>
                <div style={{ fontSize: '14px', fontWeight: '900', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                  <User size={16} color="#2563eb" /> Enregistrement complet des informations du client
                </div>
                <form onSubmit={handleEnregistrerClient} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '150px 2fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={labelStyle}>CIVILITÉ *</label>
                      <select style={inputStyle} value={clientForm.civilite_id} onChange={(e) => setClientForm({...clientForm, civilite_id: e.target.value})} required>
                        <option value="">-- Choisir --</option>
                        {civilitesList.map((civ) => {
                          const texteAffiche = civ.abreviation || civ.libelle;
                          return (
                            <option key={civ.id} value={civ.id}>
                              {texteAffiche}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <label style={labelStyle}>NOM DU CLIENT / RAISON SOCIALE *</label>
                      <input type="text" style={inputStyle} placeholder="Ex: Jean Dupont" value={clientForm.nom} onChange={(e) => { setClientForm({...clientForm, nom: e.target.value}); setShowFormClientDropdown(true); }} onFocus={() => setShowFormClientDropdown(true)} required />
                      {showFormClientDropdown && clientForm.nom.trim().length > 0 && clientsFormFiltres.length > 0 && (
                        <div style={dropdownStyle}>
                          <div style={{ padding: '6px 12px', background: '#fef2f2', color: '#991b1b', fontSize: '11px', fontWeight: 'bold', borderBottom: '1px solid #fecaca' }}>⚠️ Client existant :</div>
                          {clientsFormFiltres.map((c, idx) => (
                            <div key={c.id || idx} onClick={() => { setClientForm(prev => ({ ...prev, nom: c.nom, telephone: c.telephone || prev.telephone, civilite_id: c.civilite_id || prev.civilite_id })); setShowFormClientDropdown(false); }} style={dropdownItemStyle}>
                              👤 <strong>{c.nom}</strong> ({c.telephone || 'Sans tél'})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={labelStyle}>NIF / NUMÉRO FISCAL</label>
                      <input type="text" style={inputStyle} placeholder="Numéro fiscal" value={clientForm.nif} onChange={(e) => setClientForm({...clientForm, nif: e.target.value})} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={labelStyle}>TÉLÉPHONE *</label>
                      <input type="text" inputMode="numeric" pattern="[0-9]*" style={inputStyle} placeholder="Ex: 90000000" value={clientForm.telephone} onChange={(e) => setClientForm({...clientForm, telephone: e.target.value.replace(/\D/g, '')})} required />
                    </div>
                    <div>
                      <label style={labelStyle}>TYPE PIÈCE</label>
                      <select style={inputStyle} value={clientForm.piece_identite_type} onChange={(e) => setClientForm({...clientForm, piece_identite_type: e.target.value})}>
                        <option value="CNI">CNI</option><option value="PASSEPORT">Passeport</option><option value="PERMIS">Permis</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>N° DE PIÈCE *</label>
                      <input type="text" style={inputStyle} placeholder="Ex: CNI12345" value={clientForm.piece_identite_numero} onChange={(e) => setClientForm({...clientForm, piece_identite_numero: e.target.value})} required />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px', alignItems: 'end' }}>
                    <div>
                      <label style={labelStyle}>EMAIL</label>
                      <input type="email" style={inputStyle} placeholder="client@domaine.com" value={clientForm.email} onChange={(e) => setClientForm({...clientForm, email: e.target.value})} />
                    </div>
                    <div>
                      <label style={labelStyle}>ADRESSE</label>
                      <input type="text" style={inputStyle} placeholder="Ville, Quartier, Rue" value={clientForm.adresse} onChange={(e) => setClientForm({...clientForm, adresse: e.target.value})} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px', gap: '10px' }}>
                    <button type="submit" disabled={loading} style={{ ...btnSubmitStyle, width: 'auto', padding: '10px 24px', background: '#2563eb' }}>
                      <Plus size={16} /> Enregistrer le client
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'checkin_form' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '1000px', margin: '0 auto' }}>
              <div style={{ ...contentCardStyle, padding: '24px', border: '2px solid #059669' }}>
                <div style={{ fontSize: '14px', fontWeight: '900', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                  <Bed size={16} color="#059669" /> Recherche du client & Affectation de la chambre (Walk-in Check-in)
                </div>
                <form onSubmit={handleValiderAffectationChambre} style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '16px' }}>
                  
                  {/* CHAMP CIVILITÉ AUTOMATIQUE (Lecture seule ou pré-rempli si client existant) */}
                  <div>
                    <label style={labelStyle}>Civilité *</label>
                    <select 
                      style={{ ...inputStyle, background: sejourForm.client_id ? '#f1f5f9' : '#fff' }} 
                      value={sejourForm.civilite_id} 
                      onChange={(e) => setSejourForm({...sejourForm, civilite_id: e.target.value})}
                      required
                    >
                      <option value="">-- Choisir --</option>
                      {civilitesList.map((civ) => (
                        <option key={civ.id} value={civ.id}>
                          {civ.abreviation || civ.libelle}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* CHAMP DE RECHERCHE DU CLIENT (SE REMPLIT ET MET À JOUR LA CIVILITÉ AUTOMATIQUEMENT) */}
                  <div style={{ position: 'relative' }}>
                    <label style={labelStyle}>Rechercher le client enregistré (par nom ou téléphone)</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Tapez le nom du client..." 
                      value={sejourForm.client_recherche} 
                      onChange={(e) => { 
                        setSejourForm(prev => ({ ...prev, client_recherche: e.target.value, client_id: '', civilite_id: '' })); 
                        setShowClientDropdown(true); 
                      }} 
                      onFocus={() => setShowClientDropdown(true)} 
                      required 
                    />
                    {showClientDropdown && sejourForm.client_recherche.trim().length > 0 && clientsFiltres.length > 0 && (
                      <div style={dropdownStyle}>
                        {clientsFiltres.map((c, idx) => {
                          const civObj = civilitesList.find(civ => civ.id === c.civilite_id);
                          const abreviationClient = civObj ? `[${civObj.abreviation || civObj.libelle}] ` : '';
                          return (
                            <div 
                              key={c.id || idx} 
                              onClick={() => { 
                                setSejourForm(prev => ({ 
                                  ...prev, 
                                  client_id: c.id, 
                                  civilite_id: c.civilite_id || '', // 👈 REMPLISSAGE AUTOMATIQUE DE LA CIVILITÉ
                                  client_recherche: c.nom.toUpperCase() 
                                })); 
                                setShowClientDropdown(false); 
                              }} 
                              style={dropdownItemStyle}
                            >
                              <span style={{ color: '#2563eb', fontWeight: 'bold' }}>{abreviationClient}</span>
                              <strong>{c.nom.toUpperCase()}</strong> ({c.telephone || 'Sans tél'})
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div style={{ gridColumn: 'span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Date d'arrivée (🔒 Figée sur aujourd'hui - Walk-in)</label>
                      <input type="date" style={{ ...inputStyle, background: '#f1f5f9', cursor: 'not-allowed', color: '#64748b', fontWeight: 'bold' }} value={aujourdHui} readOnly disabled />
                    </div>
                    <div>
                      <label style={labelStyle}>Date de départ prévue</label>
                      <input type="date" style={inputStyle} min={aujourdHui} value={sejourForm.date_depart_prevue} onChange={(e) => setSejourForm({...sejourForm, date_depart_prevue: e.target.value})} required />
                    </div>
                  </div>

                  <div style={{ gridColumn: 'span 2', position: 'relative' }}>
                    <label style={labelStyle}>Rechercher / Sélectionner la chambre libre</label>
                    <input 
                      type="text" 
                      style={inputStyle} 
                      placeholder="Tapez le numéro ou la catégorie de la chambre..." 
                      value={chambreRecherche} 
                      onChange={(e) => {
                        setChambreRecherche(e.target.value);
                        setShowChambreDropdown(true);
                        if (!e.target.value.trim()) {
                          setSejourForm(prev => ({ ...prev, chambre_id: '' }));
                        }
                      }} 
                      onFocus={() => setShowChambreDropdown(true)} 
                      required 
                    />
                    {showChambreDropdown && chambresFiltrees.length > 0 && (
                      <div style={dropdownStyle}>
                        {chambresFiltrees.map((ch, idx) => (
                          <div 
                            key={ch.id || idx} 
                            onClick={() => { 
                              setSejourForm(prev => ({ ...prev, chambre_id: ch.id }));
                              setChambreRecherche(`Chambre N° ${ch.numero} (${ch.categorie_nom || ch.categorie || 'Standard'}) - ${ch.tarif_base ? Number(ch.tarif_base).toLocaleString() + ' F' : ''}`);
                              setShowChambreDropdown(false); 
                            }} 
                            style={dropdownItemStyle}
                          >
                            🏨 <strong>Chambre N° {ch.numero}</strong> ({ch.categorie_nom || ch.categorie || 'Standard'}) - <span style={{ color: '#059669', fontWeight: 'bold' }}>{ch.tarif_base ? `${Number(ch.tarif_base).toLocaleString()} F` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={labelStyle}><Users size={12} /> Nombre d'Adultes</label>
                    <input type="number" min="1" style={inputStyle} value={sejourForm.nombre_adultes} onChange={(e) => setSejourForm({...sejourForm, nombre_adultes: e.target.value})} required />
                  </div>
                  <div>
                    <label style={labelStyle}><Users size={12} /> Nombre d'Enfants</label>
                    <input type="number" min="0" style={inputStyle} value={sejourForm.nombre_enfants} onChange={(e) => setSejourForm({...sejourForm, nombre_enfants: e.target.value})} required />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Type de séjour</label>
                    <select style={inputStyle} value={sejourForm.type_sejour} onChange={(e) => setSejourForm({...sejourForm, type_sejour: e.target.value})}>
                      <option value="NUITEE">Nuitée classique</option>
                      <option value="DAY_USE">Day Use (Courte durée - Max 5h)</option>
                    </select>
                  </div>

                  <div style={{ gridColumn: 'span 2', background: '#f0fdf4', padding: '12px 16px', borderRadius: '8px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input 
                      type="checkbox" 
                      id="ignorer_frais_early" 
                      checked={ignorerFraisEarly} 
                      onChange={(e) => setIgnorerFraisEarly(e.target.checked)} 
                      style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#059669' }} 
                    />
                    <label htmlFor="ignorer_frais_early" style={{ fontSize: '11px', fontWeight: '900', color: '#166534', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Gift size={14} color="#059669" /> Offrir les frais de check-in matinal (Geste commercial / Gracieux - 0 F)
                    </label>
                  </div>

                  {sejourForm.chambre_id && composantsChambre.length > 0 && (
                    <div style={{ gridColumn: 'span 2', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                      <div style={{ fontWeight: '900', fontSize: '12px', color: '#1e293b', marginBottom: '10px' }}>📋 Composition tarifaire de la chambre :</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', background: 'white', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                            <th style={{ padding: '8px', width: '40px', textAlign: 'center' }}>Inclure</th>
                            <th style={{ padding: '8px' }}>Désignation / Élément</th>
                            <th style={{ padding: '8px' }}>Quantité</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>Prix Unitaire</th>
                          </tr>
                        </thead>
                        <tbody>
                          {composantsChambre.map((comp, idx) => {
                            const key = comp.id ? `${comp.id}-${idx}` : `comp-${idx}`;
                            const estCoche = composantsSelectionnes[key] !== false;
                            return (
                              <tr key={key} style={{ borderBottom: '1px solid #f1f5f9', background: estCoche ? 'white' : '#fef2f2' }}>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <input type="checkbox" checked={estCoche} onChange={(e) => setComposantsSelectionnes(prev => ({ ...prev, [key]: e.target.checked }))} style={{ cursor: 'pointer', width: '15px', height: '15px' }} />
                                </td>
                                <td style={{ padding: '8px', fontWeight: '700', color: estCoche ? '#1e293b' : '#94a3b8', textDecoration: estCoche ? 'none' : 'line-through' }}>{comp.designation || comp.libelle || 'Prestation'}</td>
                                <td style={{ padding: '8px', color: '#475569' }}>{comp.quantite || 1}</td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{Number(comp.prix_unitaire || comp.prix_defaut || 0).toLocaleString()} F</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', background: 'white', padding: '10px 14px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                        <div>
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '800' }}>Tarif de base calculé : </span>
                          <span style={{ fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>{tarifDeBaseCalcule.toLocaleString()} F / nuit</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '900', color: '#2563eb' }}>Tarif Nuit Négocié :</label>
                          <input type="number" value={tarifNegocieManuel} placeholder={tarifDeBaseCalcule} onChange={(e) => setTarifNegocieManuel(e.target.value)} style={{ width: '120px', padding: '6px', borderRadius: '4px', border: '1px solid #2563eb', fontWeight: '900', color: '#2563eb', fontSize: '12px', textAlign: 'right' }} />
                          <span style={{ fontSize: '11px', fontWeight: 'bold' }}>F</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div style={{ gridColumn: 'span 2', marginTop: '8px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="submit" disabled={loading} style={{ ...btnSubmitStyle, background: '#059669', width: 'auto', padding: '12px 24px' }}>
                      {loading ? 'Validation en cours...' : 'Valider l\'affectation & Clôturer le Check-in'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

const headerStyle = { background: 'white', padding: '15px 30px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const contentCardStyle = { background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', overflow: 'hidden' };
const btnTabStyle = { background: 'transparent', color: '#64748b', border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '800', fontSize: '11px' };
const btnTabActiveStyle = { background: 'white', color: '#1e293b', border: 'none', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '900', fontSize: '11px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '12px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#475569', display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const btnSubmitStyle = { width: '100%', padding: '12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '900', fontSize: '13px', boxShadow: '0 2px 4px rgba(37,99,235,0.2)' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '150px', overflowY: 'auto' };
const dropdownItemStyle = { padding: '8px 12px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' };
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '10px 20px', borderRadius: '8px', boxShadow: '0 10px 15px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', fontSize: '12px', animation: 'slideInToast 0.3s ease-out' };

export default CheckInCheckout;